import { create } from 'zustand';

import { findSectionByPath } from '@/app/sections';
import { useSessionStore } from '@/auth/token-store';

import { STRUGGLE_HOT_MS } from '../timing';
import type { InlineHostKey } from '../tours/types';
import { createStruggleScorer, type StruggleSignal, type StruggleTrip } from './score';

/**
 * The struggle bus (critic C6): collectors report here, `StruggleWatch` and
 * the inline slot take the trips. In memory only, per tab, reset by a clinic
 * switch; nothing here is sent anywhere.
 */

interface StruggleState {
  /** A page-level trip waiting to be offered (or given up on). */
  trip: StruggleTrip | null;
  /** Inline-form trips, offered on that form's next open. */
  inline: Partial<Record<InlineHostKey, StruggleTrip>>;
  /** When each section last saw a signal (the Help menu's «کمک برای همین قسمت»). */
  lastSignalAt: Record<string, number>;
}

const scorer = createStruggleScorer();

export const useStruggleStore = create<StruggleState>()(() => ({
  trip: null,
  inline: {},
  lastSignalAt: {},
}));

/** The section key of a pathname (`patients`, `crm`…), or `other`. */
export function sectionKeyOf(pathname: string): string {
  return findSectionByPath(pathname)?.key ?? 'other';
}

/** A collector saw something. Ignored while a support session views the panel. */
export function reportStruggle(signal: StruggleSignal, now = Date.now()): void {
  if (useSessionStore.getState().impersonatedBy) return;
  const trip = scorer.push(signal, now);
  useStruggleStore.setState((s) => {
    const lastSignalAt = { ...s.lastSignalAt, [signal.section]: now };
    if (!trip) return { lastSignalAt };
    if (trip.host) return { lastSignalAt, inline: { ...s.inline, [trip.host]: trip } };
    return { lastSignalAt, trip };
  });
}

/**
 * Something worked (a mutation succeeded, a tour was finished, an inline form
 * was used): the counts start afresh. `host` clears only that form's abandons
 * (and its waiting offer).
 */
export function reportProgress(scope?: { host: InlineHostKey }): void {
  scorer.reset(scope);
  if (scope) {
    useStruggleStore.setState((s) => {
      if (!s.inline[scope.host]) return s;
      const inline = { ...s.inline };
      delete inline[scope.host];
      return { inline };
    });
    return;
  }
  useStruggleStore.setState((s) =>
    Object.keys(s.lastSignalAt).length === 0 && s.trip === null
      ? s
      : { lastSignalAt: {}, trip: null },
  );
}

/** The page trip, handed over once. */
export function takeTrip(): StruggleTrip | null {
  const trip = useStruggleStore.getState().trip;
  if (trip) useStruggleStore.setState({ trip: null });
  return trip;
}

/** An inline form's waiting trip, handed over once. */
export function takeInlineTrip(host: InlineHostKey): StruggleTrip | null {
  const trip = useStruggleStore.getState().inline[host] ?? null;
  if (trip) {
    useStruggleStore.setState((s) => {
      const inline = { ...s.inline };
      delete inline[host];
      return { inline };
    });
  }
  return trip;
}

/** Whether `section` saw a struggle signal within `STRUGGLE_HOT_MS` of `now`. */
export function sectionHot(section: string, now = Date.now()): boolean {
  const at = useStruggleStore.getState().lastSignalAt[section];
  return at !== undefined && now - at < STRUGGLE_HOT_MS;
}

/** Whether `section` saw a struggle signal in the last few minutes (the Help menu's wording). */
export function useSectionHot(section: string): boolean {
  // Subscribed for the re-render when a signal arrives; the answer is the
  // same pure reading the rest of the panel uses.
  useStruggleStore((s) => s.lastSignalAt[section]);
  return sectionHot(section);
}

/** Test seam, and a clinic switch. */
export function resetStruggle(): void {
  scorer.clear();
  useStruggleStore.setState({ trip: null, inline: {}, lastSignalAt: {} });
}

useSessionStore.subscribe((state, prev) => {
  if (state.activeClinicId !== prev.activeClinicId) resetStruggle();
});
